import {
  action,
  computed,
  makeAutoObservable,
  observable,
  reaction,
} from "mobx";
import { CheckOutDisplayTypes, UpsertCheckoutActiveStep } from "../enums";
import CountryStore from "./CountryStore";
import { ToastService } from "@mindmemobile/mm-shared-global";
import { CheckoutDetailPageModel } from "../models/CheckoutDetailPageModel";
import { CheckOutSellPageModel } from "../models/CheckOutSellPageModel";
import { CheckOutAfterPayModel } from "../models/CheckOutAfterPayModel";
import {
  BasicColors,
  CheckoutBrandingModel,
  CheckoutOptionsPageModel,
  CustomColors,
  DonationCheckoutModel,
} from "../models/CheckoutOptionsPageModel";
import {
  CheckoutPaymentFormModel,
  CheckoutPaymentPageModel,
} from "../models/CheckoutPaymentPageModel";
import {
  MerchantAccount,
  MerchantAccountWithPaySettingsModel,
} from "../models/MerchantApplicationInputModel";
import { SellerModel } from "../models/SellerModels";
import { ItemDetailInputModel } from "../models/ItemDetailInputModel";
import { DonationPricingPageInputsModel } from "../models/DonationAndItemModel";
import { CustomerFacingCheckOutModel } from "../models/PurchaseModel";
import { CustomerFacingCheckOutItemModel } from "../models/PriceUtility";
import {
  IAPICheckOutSaveUpdateModel,
  IAPIRelatedCheckoutGridModel,
} from "../interfaces/IAPICheckoutModel";
import AuthService from "../services/AuthService";
import { MediaModel } from "../models/MediaModel";
import ListInterestTagStore from "./ListInterestTagStore";

export default class CheckOutStore {
  @observable activeStep: UpsertCheckoutActiveStep;
  @observable detailPage: CheckoutDetailPageModel;
  @observable sellPage: CheckOutSellPageModel;
  @observable headerCaption: string;
  @observable isSaveUpdateInProgress: boolean = false;
  @observable afterPayPage: CheckOutAfterPayModel;
  @observable paymentPageData: CheckoutPaymentPageModel;
  @observable optionsPage: CheckoutOptionsPageModel;
  @observable merchantAccounts: MerchantAccount[] = [];
  @observable sellers: SellerModel[] = [];
  @observable gridData: CheckoutRelatedGridModel[] = [];
  @observable isDonation: boolean = false;
  @observable donationdetail: ItemDetailInputModel;
  @observable donationPricingData: DonationPricingPageInputsModel;

  countryStore: CountryStore;
  listInterestTagStore: ListInterestTagStore;

  constructor(data?: CheckOutStore) {
    makeAutoObservable(this);
    this.detailPage = new CheckoutDetailPageModel();
    this.activeStep = UpsertCheckoutActiveStep.Detail;
    this.headerCaption = "Detail";
    this.sellPage = new CheckOutSellPageModel();
    this.afterPayPage = new CheckOutAfterPayModel();
    this.paymentPageData = new CheckoutPaymentPageModel();
    this.optionsPage = new CheckoutOptionsPageModel();
    this.countryStore = new CountryStore();
    this.donationPricingData = new DonationPricingPageInputsModel();
    this.donationdetail = new ItemDetailInputModel();
    this.listInterestTagStore = new ListInterestTagStore();
    if (data) this.hydrate(data);

    reaction(
      () => this.activeStep,
      () => this.updateStateBasedOnActiveStatusChange()
    );
  }

  hydrate(data: CheckOutStore) {
    this.activeStep = data.activeStep;
    this.headerCaption = data.headerCaption;
    this.detailPage = new CheckoutDetailPageModel(data.detailPage);
    this.sellPage = new CheckOutSellPageModel(data.sellPage);

    this.afterPayPage = new CheckOutAfterPayModel({
      ...data.afterPayPage,
    });

    this.paymentPageData = new CheckoutPaymentPageModel({
      ...data.paymentPageData,
      formData: new CheckoutPaymentFormModel({
        ...data.paymentPageData.formData,
      }),
    });
    this.optionsPage = new CheckoutOptionsPageModel({
      ...data.optionsPage,
      brandingData: new CheckoutBrandingModel({
        ...data.optionsPage.brandingData,
        basicColors: new BasicColors({
          ...data.optionsPage.brandingData.basicColors,
        }),
        customColors: new CustomColors({
          ...data.optionsPage.brandingData.customColors,
        }),
        donationCheckout: new DonationCheckoutModel({
          ...data.optionsPage.brandingData.donationCheckout,
        }),
      }),
    });
    this.donationPricingData = new DonationPricingPageInputsModel(
      data.donationPricingData
    );
    this.donationdetail = new ItemDetailInputModel(data.donationdetail);
  }

  @computed
  get customerFacingModel(): CustomerFacingCheckOutModel {
    const data: Partial<CustomerFacingCheckOutModel> = {
      isDeleted: false,
      isActive: this.detailPage.isActive,
      items: this.sellPage.items.map((item) =>
        CustomerFacingCheckOutItemModel.deserializeFromCheckOutItemsModel(item)
      ),
      recommendedItem: this.sellPage.recommendedItem
        ? this.sellPage.recommendedItem
        : undefined,
      paymentDetails: this.paymentPageData,
      afterPayDetails: this.afterPayPage ? this.afterPayPage : null,
      options: this.optionsPage,
      showSingleItemPrice: false,
      displayType: this.detailPage
        .selectedDisplayType as keyof typeof CheckOutDisplayTypes,
      allowIncludedItems: this.sellPage.allowIncludedItems,
      allowRequiredItems: this.sellPage.allowRequiredItems,
      allowRecommendedItems: this.sellPage.allowRecommendedItems,
      groupByBillingFrequency: this.sellPage.groupByBillingFrequency,
      merchantAccount: new MerchantAccountWithPaySettingsModel({
        name: this.detailPage.selectedMerchantAccount.name,
        gateway: this.detailPage.selectedMerchantAccount.gateway,
        merchantAccountId:
          this.detailPage.selectedMerchantAccount.merchantAccountId,
        applicationId: this.detailPage.selectedMerchantAccount.applicationId,
        paymentSettings:
          this.detailPage.selectedMerchantAccount.paymentSettings,
      }),
      isDonationOnly: this.detailPage.isDonationOnly,
      seller: this.detailPage.selectedSeller,
      donationCheckout: this.optionsPage.brandingData.donationCheckout,
      donationDetailPage: this.donationdetail,
      donationPricingPageData: this.donationPricingData,
      customOption: this.sellPage.customoption
        ? this.sellPage.customoption
        : undefined,
    };

    return new CustomerFacingCheckOutModel(data);
  }

  @action
  updateDonationValue(val: boolean) {
    this.isDonation = val;
  }

  @action
  updateDonationData(
    detail: ItemDetailInputModel,
    priceData: DonationPricingPageInputsModel,
    isDonation: boolean
  ) {
    this.donationPricingData = priceData;
    this.donationdetail = detail;
    this.isDonation = isDonation;
  }

  deserializeAPIResponse(data: IAPIRelatedCheckoutGridModel[]): void {
    ("");
    this.gridData =
      (data &&
        data.map((purchaseLink: any) =>
          CheckoutRelatedGridModel.deserializeAPIModel(purchaseLink)
        )) ||
      [];
  }

  @action
  updateMerchantAccounts(accounts: MerchantAccount[]) {
    this.merchantAccounts = accounts;
  }

  @action
  updateSellers(sellers: SellerModel[]) {
    this.sellers = sellers;
  }

  @action
  updateActiveStep(activeStep: UpsertCheckoutActiveStep) {
    this.activeStep = activeStep;
  }

  @action
  updateDetailPage(data: CheckoutDetailPageModel) {
    this.detailPage = new CheckoutDetailPageModel(data);
  }

  @action
  updateStateBasedOnActiveStatusChange() {
    switch (this.activeStep) {
      case UpsertCheckoutActiveStep.Detail:
        this.headerCaption = "Detail";
        break;
      case UpsertCheckoutActiveStep.Sell:
        this.headerCaption = "Sell";
        break;
      case UpsertCheckoutActiveStep.Pay:
        this.headerCaption = "Pay";
        break;
      case UpsertCheckoutActiveStep.AfterPay:
        this.headerCaption = "After Pay";
        break;
      case UpsertCheckoutActiveStep.Options:
        this.headerCaption = "Checkout Options";
        break;
    }
  }

  @action
  validate(step?: UpsertCheckoutActiveStep): boolean {
    const isTabChange = step ? true : false;
    const stepToValidate = isTabChange ? step : this.activeStep;
    switch (stepToValidate) {
      case UpsertCheckoutActiveStep.Detail:
        const isValidDetailPage = this.detailPage.validate();
        this.updateDetailPage(new CheckoutDetailPageModel(this.detailPage));
        return isValidDetailPage;
      case UpsertCheckoutActiveStep.Sell:
        const isValidSellPage = this.sellPage.validate();
        if (this.detailPage.isDonationOnly) {
          const hasNonDonationItem = this.sellPage.items.some(
            (item) => item.itemDetails.type !== "Donation"
          );
          if (hasNonDonationItem) {
            ToastService.showError(
              'Invalid items: "Product, Service or Membership" items are not allowed.'
            );
            return false;
          }
        }
        if (!this.detailPage.isDonationOnly) {
          const hasDonationItem = this.sellPage.items.some(
            (item) => item.itemDetails.type === "Donation"
          );
          if (hasDonationItem) {
            ToastService.showError(
              'Invalid items: "Donation" items are not allowed.'
            );
            return false;
          }
          if (
            this.detailPage.selectedDisplayType ===
            CheckOutDisplayTypes.SingleItem
          ) {
            if (this.sellPage.items.length !== 1) {
              ToastService.showError("Please select an item.");
              return false;
            }
          }
          if (
            this.detailPage.selectedDisplayType ===
            CheckOutDisplayTypes.SingleItemWithMultiplePrices
          ) {
            const uniqueItemIds = new Set(
              this.sellPage.items.map((item) => item.itemId)
            );
            const uniquePriceIds = new Set(
              this.sellPage.items.map((item) => item.priceId)
            );
            if (uniqueItemIds.size !== 1 || uniquePriceIds.size <= 1) {
              ToastService.showError(
                "Please select single item with different prices"
              );
              return false;
            }
          }
          if (
            this.detailPage.selectedDisplayType ===
              CheckOutDisplayTypes.MultipleItems ||
            this.detailPage.selectedDisplayType ===
              CheckOutDisplayTypes.MultipleItemsInbundle
          ) {
            const uniqueItemIds = new Set(
              this.sellPage.items.map((item) => item.itemId)
            );
            const uniqueItemPriceCombination = new Set(
              this.sellPage.items.map(
                (item) => `${item.itemId}-${item.priceId}`
              )
            );

            if (uniqueItemIds.size === 1) {
              ToastService.showError(
                "More than one item is required for the layout selected."
              );
              return false;
            }
            if (this.sellPage.items.length > 4) {
              ToastService.showError("A maximum of 4 items is allowed.");
              return false;
            }
            if (
              uniqueItemPriceCombination.size !== this.sellPage.items.length
            ) {
              ToastService.showError(
                "Invalid selection: Items must have unique prices."
              );
              return false;
            }
          }
        }

        this.updateSellPage(new CheckOutSellPageModel(this.sellPage));
        return isValidSellPage;
      case UpsertCheckoutActiveStep.AfterPay:
        const isValidAfterPayPage = this.afterPayPage.validate();
        this.updateAfterPayPage(new CheckOutAfterPayModel(this.afterPayPage));
        return isValidAfterPayPage;
      case UpsertCheckoutActiveStep.Pay:
        const isValidPayPage = this.paymentPageData.validate();
        this.updatePaymentPage(
          new CheckoutPaymentPageModel(this.paymentPageData)
        );
        return isValidPayPage;
      case UpsertCheckoutActiveStep.Options:
        const isValidOptions = this.optionsPage.validate();
        this.updateOptionsPage(new CheckoutOptionsPageModel(this.optionsPage));
        if (!isValidOptions) {
          ToastService.showError(
            "Please fill all the required fields in the Options step before saving"
          );
        }
        return isValidOptions;
      default:
        return false;
    }
  }

  @action
  validatePreviousSteps(currentStep: UpsertCheckoutActiveStep): {
    isValid: boolean;
    errorStep?: UpsertCheckoutActiveStep;
  } {
    const steps = [
      UpsertCheckoutActiveStep.Detail,
      UpsertCheckoutActiveStep.Sell,
      UpsertCheckoutActiveStep.Pay,
      UpsertCheckoutActiveStep.AfterPay,
      UpsertCheckoutActiveStep.Options,
    ];

    const currentStepIndex = steps.indexOf(currentStep);
    for (let i = 0; i < currentStepIndex; i++) {
      const step = steps[i];
      if (!this.validate(step)) {
        return { isValid: false, errorStep: step };
      }
    }
    return { isValid: true };
  }

  @action
  reset() {
    this.detailPage = new CheckoutDetailPageModel({ isActive: true });
    this.activeStep = UpsertCheckoutActiveStep.Detail;
    this.headerCaption = "Detail";
    this.isSaveUpdateInProgress = false;
    this.sellPage = new CheckOutSellPageModel();
    this.afterPayPage = new CheckOutAfterPayModel();

    this.paymentPageData = new CheckoutPaymentPageModel();
    this.optionsPage = new CheckoutOptionsPageModel();
  }

  @action
  toggleSaveUpdateInProgress() {
    this.isSaveUpdateInProgress = !this.isSaveUpdateInProgress;
  }

  updateSellPage(data: CheckOutSellPageModel) {
    this.sellPage = new CheckOutSellPageModel(data);
  }

  updateAfterPayPage(data: CheckOutAfterPayModel) {
    this.afterPayPage = new CheckOutAfterPayModel(data);
  }

  @action
  updatePaymentPage(data: CheckoutPaymentPageModel) {
    this.paymentPageData = new CheckoutPaymentPageModel(data);
  }

  @action
  updateOptionsPage(data: CheckoutOptionsPageModel) {
    this.optionsPage = new CheckoutOptionsPageModel(data);
  }

  createAPIModel(id: string = ""): IAPICheckOutSaveUpdateModel {
    return {
      ...this.detailPage.createAPIModel(),
      id: id,
      accountId: AuthService.authData.accountId,
      payDetails: this.paymentPageData.createApiModel(),
      sellDetails: this.sellPage.createAPIModel(),
      afterPayDetails: this.afterPayPage.createAPIModel(),
      options: this.optionsPage.createAPIModel(this.detailPage.isDonationOnly),
      mediaPlacement:
        this.optionsPage.brandingData.donationCheckout.createAPIModel(),
      media: this.optionsPage.brandingData.donationCheckout.media[0]?.fullURL
        ? this.optionsPage.brandingData.donationCheckout.media[0].createAPIModel()
        : null,
      customOption: this.sellPage.customoption?.createAPIModel(),
      lists: this.optionsPage.selectedList.map((c) => c.createAPIModel()),
      tags: this.optionsPage.selectedTag.map((c) => c.createAPIModel()),
      interests: [],
    };
  }

  constructStoreFromAPIModel(data: IAPICheckOutSaveUpdateModel) {
    this.detailPage = CheckoutDetailPageModel.deserializeAPIModel(data);
    this.sellPage = CheckOutSellPageModel.deserializeAPIModel(
      data.sellDetails,
      data.customOption
    );
    this.afterPayPage = CheckOutAfterPayModel.deserializeAPIModel(
      data.afterPayDetails
    );
    this.paymentPageData = CheckoutPaymentPageModel.deserializeAPIModel(
      data.payDetails
    );
    if (data.isDonationOnly) {
      this.optionsPage = CheckoutOptionsPageModel.deserializeDonationData(
        data.options,
        data.media || new MediaModel(),
        data.mediaPlacement
      );
      if (this.sellPage.items?.length > 0) {
        const donationItems = this.sellPage.items[0];
        if (
          donationItems.itemDetails?.type === "Donation" &&
          donationItems.itemDetails.donationDetailPageData &&
          donationItems.itemDetails.donationPriceSettings
        ) {
          this.updateDonationData(
            donationItems.itemDetails.donationDetailPageData,
            donationItems.itemDetails.donationPriceSettings,
            true
          );
        }
      }
    } else {
      this.optionsPage = CheckoutOptionsPageModel.deserializeAPIModel(
        data.options,
        data.lists,
        data.tags
      );
    }
  }
}

export class CheckoutRelatedGridModel {
  id: string;
  isActive: boolean;
  name: string;
  lastPurchase: string;
  minPrice: number;
  maxPrice: number;
  constructor(data?: Partial<CheckoutRelatedGridModel>) {
    this.id = data?.id || "";
    this.isActive = data?.isActive || false;
    this.name = data?.name || "";
    this.lastPurchase = data?.lastPurchase || "";
    this.minPrice = data?.minPrice || 0;
    this.maxPrice = data?.maxPrice || 0;
  }

  static deserializeAPIModel(
    data: IAPIRelatedCheckoutGridModel
  ): CheckoutRelatedGridModel {
    const mappedData: Partial<CheckoutRelatedGridModel> = {
      id: data.id,
      isActive: data.isActive,
      name: data.name,
      lastPurchase: data.lastPurchaseDate
        ? new Date(data.lastPurchaseDate).toLocaleDateString()
        : "Never",
      minPrice: data.minPrice,
      maxPrice: data.maxPrice,
    };

    return new CheckoutRelatedGridModel(mappedData);
  }
}
